import { z } from 'zod'
import { dataNominal } from './comum.js'

/** Fila única dos e-mails que o worker entrega fora do request HTTP. */
export const FILA_EMAIL_TRANSACIONAL = 'dv-email-transacional'

/** Link de e-mail: só HTTP ou HTTPS. `javascript:` e companhia não passam. */
const urlHttp = z.url().refine((valor) => valor.startsWith('https://') || valor.startsWith('http://'), {
  message: 'O link do e-mail deve usar HTTP ou HTTPS.',
})

const destino = z.object({
  para: z.email(),
  nome: z.string().trim().min(1),
  link: urlHttp,
})

/**
 * O AVISO de um evento (DS-637): o mesmo layout dos outros e-mails, com um título, até cinco
 * parágrafos e, quando há destino que a pessoa abre, o botão. `link` é opcional (E-o): sem ele,
 * o worker não desenha botão. `obrigatorio` escolhe o rodapé: o obrigatório diz que não se
 * desliga; o de preferência leva a Meu perfil.
 */
const aviso = z.object({
  template: z.literal('aviso'),
  para: z.email(),
  nome: z.string().trim().min(1),
  assunto: z.string().trim().min(1).max(150),
  titulo: z.string().trim().min(1).max(150),
  paragrafos: z.array(z.string().trim().min(1).max(1000)).min(1).max(5),
  chamada: z.string().trim().min(1).max(40),
  link: urlHttp.optional(),
  linkPreferencias: urlHttp,
  obrigatorio: z.boolean(),
})

/**
 * O RESUMO DIÁRIO das 07:30 (DS-639): até quatro seções (a quarta, "Aguardando autorização para faturar", desde a
 * D-209), cada uma com até 15 itens e a linha
 * "e mais N". Cada item pode levar o próprio link (o pedido recortado na fila, A-09); o link
 * da seção é complementar. O rodapé é por seção: a obrigatória não se desliga, a de
 * preferência leva a Meu perfil.
 */
const resumoDiario = z.object({
  template: z.literal('resumo_diario'),
  para: z.email(),
  nome: z.string().trim().min(1),
  data: dataNominal,
  secoes: z.array(z.object({
    titulo: z.string().trim().min(1).max(120),
    itens: z.array(z.object({
      texto: z.string().trim().min(1).max(300),
      link: urlHttp.optional(),
    })).min(1).max(15),
    restantes: z.number().int().nonnegative(),
    chamada: z.string().trim().min(1).max(40),
    link: urlHttp,
    obrigatoria: z.boolean(),
  })).min(1).max(4),
  linkPreferencias: urlHttp,
})

const alertaOperacional = z.object({
  para: z.email(),
  estado: z.string().trim().min(1),
  observadoEm: z.iso.datetime({ offset: true }),
})

/**
 * O job carrega intenção, não HTML pronto. Assim assunto, texto e marca têm uma
 * única fonte no worker e uma alteração de template não espalha versões pela API.
 */
export const emailTransacional = z.discriminatedUnion('template', [
  destino.extend({ template: z.literal('convite') }),
  destino.extend({ template: z.literal('convite_portal') }),
  /**
   * Um acesso NOVO para quem já tem senha no portal (PK-06A): o convite de senha abriria
   * "Convite inválido ou vencido", porque o aceite é só de quem ainda está convidado.
   */
  destino.extend({ template: z.literal('acesso_portal'), empresa: z.string().trim().min(1) }),
  /**
   * `publico` (DS-632): o dreno do Portal marca `'portal'` e o texto passa a falar do Portal.
   * Opcional e aditivo: a API continua sem mandar o campo, e o worker anterior o descarta.
   */
  destino.extend({ template: z.literal('redefinicao_de_senha'), publico: z.enum(['interno', 'portal']).optional() }),
  destino.extend({
    template: z.literal('solicitacao_documento_comissao'),
    documento: z.enum(['NF', 'RPA']),
  }),
  alertaOperacional.extend({ template: z.literal('queda_waha') }),
  aviso,
  resumoDiario,
])

export type EmailTransacional = z.infer<typeof emailTransacional>
